/**
 * The fleet-wide Benchmarks screen (OQA, owner 2026-10-02: "benchmarks visible on
 * the site" — discoverable from the top nav, not only by scrolling each organism's
 * page). One section per organism: its own `BenchmarksPanel` (live W(t) vs
 * equal-weight buy-and-hold, B&H monthly, core-only and cash from the episode's
 * genesis; costs, consistency, regimes, rolling 30-day gate), reading the same
 * `index.json.refs.benchmarks` object the organism screen does — no new data.
 */

import type { Router } from "../router/router";
import { resolveAllRoleLocations } from "../data/roleDirectory";
import { getRoleSnapshot } from "../data/RoleSnapshotRegistry";
import { BenchmarksPanel } from "../panels/BenchmarksPanel";
import { renderNotPublishedYet } from "../panels/NotPublishedYet";
import { renderScreenHeader } from "./screenHeader";
import { attachTooltip } from "../ui/tooltip";

export class BenchmarksScreen {
  private readonly panels: BenchmarksPanel[] = [];
  private disposed = false;

  constructor(
    private readonly container: HTMLElement,
    private readonly router: Router,
  ) {}

  async mount(): Promise<void> {
    this.container.innerHTML = "";
    this.container.appendChild(
      renderScreenHeader(
        "Benchmarks",
        "Each fly's live W(t) against equal-weight buy-and-hold, buy-and-hold rebalanced " +
          "monthly, its own core-only allocation and cash — same genesis, opening capital and " +
          "cost model — plus costs, consistency, regimes and the rolling 30-day gate.",
      ),
    );
    const locations = await resolveAllRoleLocations();
    if (this.disposed) return;
    if (locations.length === 0) {
      this.container.appendChild(
        renderNotPublishedYet("Benchmarks", "No organism has been published yet."),
      );
      return;
    }
    for (const location of locations) {
      const section = document.createElement("section");
      section.className = "obs-panel obs-benchmarks-screen-section";
      const header = document.createElement("div");
      header.className = "obs-panel-header";
      const title = document.createElement("a");
      title.className = "obs-panel-title";
      title.href = `#/organism/${location.role}`;
      title.textContent = location.displayName;
      title.addEventListener("click", (event) => {
        event.preventDefault();
        this.router.navigate({ kind: "organism", role: location.role });
      });
      attachTooltip(title, `Open ${location.displayName}'s full page.`);
      header.appendChild(title);
      section.appendChild(header);
      const body = document.createElement("div");
      body.className = "obs-panel-body";
      section.appendChild(body);
      this.container.appendChild(section);

      const snapshot = await getRoleSnapshot(location);
      if (this.disposed) return;
      if (!snapshot) {
        body.appendChild(
          renderNotPublishedYet(location.displayName, "This organism has not been published yet."),
        );
        continue;
      }
      const panel = new BenchmarksPanel(body, snapshot);
      this.panels.push(panel);
      void panel.mount();
    }
  }

  dispose(): void {
    this.disposed = true;
    for (const panel of this.panels) panel.dispose();
    this.panels.length = 0;
  }
}
